/** New-region dialog: pick any tenant + name (+ optional custom_id, card
 *  arrangement), POST, then navigate to the created region. */
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ConsoleApiError } from '@/api/client';
import * as regionsApi from '@/api/regions';
import type { CardArrangement } from '@/api/regions';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';
import { useSnackbar } from '@/components/Snackbar';
import { useCreateDialogTenant } from '@/scope/useCreateDialogTenant';

export function RegionCreateDialog({
  open,
  onClose,
  pageTenants,
}: {
  open: boolean;
  onClose: () => void;
  /** The page's operators (D9) — see `useCreateDialogTenant`. */
  pageTenants?: TenantPickerValue[];
}) {
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const [tenant, setTenant, resetTenant] = useCreateDialogTenant(open, pageTenants);
  const [name, setName] = useState('');
  const [customId, setCustomId] = useState('');
  const [cardArrangement, setCardArrangement] = useState<CardArrangement>('horizontal');
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    resetTenant();
    setName('');
    setCustomId('');
    setCardArrangement('horizontal');
    setError(null);
  };

  // Page tenants: every open starts a fresh form on the page's tenant, so a
  // Cancelled pick (and the fields that hang on it) never carries over.
  useEffect(() => {
    if (open && pageTenants !== undefined) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const mutation = useMutation({
    mutationFn: () =>
      regionsApi.create({
        tour_operator: tenant!.id,
        name: name.trim(),
        custom_id: customId.trim() || null,
        card_arrangement: cardArrangement,
      }),
    onSuccess: (region) => {
      snackbar.show('Region created.', 'success');
      qc.invalidateQueries({ queryKey: ['regions'] });
      reset();
      onClose();
      navigate(`/regions/${region.id}`);
    },
    onError: (e) => setError(e instanceof ConsoleApiError ? e.message : 'Create failed.'),
  });

  const canSubmit = tenant !== null && name.trim().length > 0 && !mutation.isPending;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>New region</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TenantPicker value={tenant} featured={pageTenants} onChange={setTenant} label="Operator" placeholder="Select operator" />
          <TextField
            label="Name" size="small" fullWidth value={name} required
            onChange={(e) => setName(e.target.value)} inputProps={{ maxLength: 256 }}
          />
          <TextField
            label="Custom ID" size="small" fullWidth value={customId}
            onChange={(e) => setCustomId(e.target.value)} inputProps={{ maxLength: 256 }}
          />
          <TextField
            select label="Card arrangement" size="small" sx={{ width: 260 }} value={cardArrangement}
            onChange={(e) => setCardArrangement(e.target.value as CardArrangement)}
          >
            <MenuItem value="horizontal">Horizontal</MenuItem>
            <MenuItem value="vertical">Vertical</MenuItem>
          </TextField>
          {error && <Box sx={{ color: 'error.main', fontSize: 13 }}>{error}</Box>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!canSubmit} onClick={() => mutation.mutate()}>
          {mutation.isPending ? 'Creating…' : 'Create region'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
